// Stl viewer ultra premium pro.jsx


import React, { useEffect, useRef, useState, Suspense } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Preload, Environment } from "@react-three/drei";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { MeshStandardMaterial, MeshPhysicalMaterial, Mesh, Vector3, Box3, Color } from "three";

const DEFAULT_COLOR = 0xCCCCCC;
const DEFAULT_BG = "#bdbdbeff";

// ---helpers ---


const calculateVolume = (geometry) => {
  if (!geometry?.attributes?.position) return 0;
  let volume = 0;
  const pos = geometry.attributes.position.array;
  for (let i = 0; i < pos.length; i += 9) {
    const p1 = new Vector3(pos[i], pos[i + 1], pos[i + 2]);
    const p2 = new Vector3(pos[i + 3], pos[i + 4], pos[i + 5]);
    const p3 = new Vector3(pos[i + 6], pos[i + 7], pos[i + 8]);
    volume += p1.dot(p2.clone().cross(p3)) / 6;
  }
  return Math.abs(volume);
};

const calculateSurfaceArea = (geometry) => {
  if (!geometry?.attributes?.position) return 0;
  let area = 0;
  const pos = geometry.attributes.position.array;
  for (let i = 0; i < pos.length; i += 9) {
    const p1 = new Vector3(pos[i], pos[i + 1], pos[i + 2]);
    const p2 = new Vector3(pos[i + 3], pos[i + 4], pos[i + 5]);
    const p3 = new Vector3(pos[i + 6], pos[i + 7], pos[i + 8]);
    area += p2.clone().sub(p1).cross(p3.clone().sub(p1)).length() / 2;
  }
  return area;
};

// ---loading overlay ---


const LoaderOverlay = () => (
  <div style={{
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0, 0, 0, 0.25)",
    zIndex: 1000,
    pointerEvents: "none"
  }}>
    <div className="spinner-border text-light" role="status" style={{ width: "3rem", height: "3rem" }}>
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);


// ---material factory ---


const createMaterial = (shading, currentColor) => {
  const color = new Color(DEFAULT_COLOR);
  switch (shading) {
    case "matte":
      return new MeshStandardMaterial({
        color,
        roughness: 0.85,  
        metalness: 0.05,  
        envMapIntensity: 0.2 
      });

    case "neutral":
      return new MeshStandardMaterial({
        color,
        roughness: 0.5,   
        metalness: 0.2,   
        envMapIntensity: 0.3 
      });

    case "metal":
      return new MeshStandardMaterial({
        color,
        roughness: 0.2,  
        metalness: 1,     
        envMapIntensity: 1 
      });

    case "ceramic":
      return new MeshPhysicalMaterial({
        color,
        roughness: 0.3,      
        metalness: 0.1,
        clearcoat: 0.5,        
        clearcoatRoughness: 0.2,
        envMapIntensity: 0.5
      });

    case "carbon":
      return new MeshStandardMaterial({
        color: new Color("#1c1c1c"),
        roughness: 0.6,    
        metalness: 0.3,    
        envMapIntensity: 0.3
      });

    case "glass":
      return new MeshPhysicalMaterial({
        color,
        roughness: 0.05,     
        metalness: 0,
        transmission: 0.9,   
        opacity: 0.6,
        transparent: true,
        clearcoat: 0.2,
        clearcoatRoughness: 0,
        envMapIntensity: 0.8
      });

    default:
      return new MeshStandardMaterial({
        color,
        roughness: 0.7,
        metalness: 0.3,
        envMapIntensity: 0.2
      });
  }

};

const STLViewerUltraPremiumPro = ({ url, currentColor, onStatsUpdate = () => { }, currentDencity = 0 }) => {
  const containerRef = useRef();
  const [model, setModel] = useState(null);
  const [scaleArr, setScaleArr] = useState([1, 1, 1]);
  const [positionArr, setPositionArr] = useState([0, 0, 0]);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [wireframe, setWireframe] = useState(false);
  const [shading, setShading] = useState("matte");
  const [bg, setBg] = useState(DEFAULT_BG);
  const [autoRotate, setAutoRotate] = useState(false);
  const primitiveRef = useRef();

  // ---loader ---


  useEffect(() => {
    if (!url) return;
    setModel(null);
    setLoadingProgress(0);

    const ext = url.split("?")[0].split(".").pop().toLowerCase();

    const processMesh = (mesh) => {
      const box = new Box3().setFromObject(mesh);
      const center = new Vector3();
      box.getCenter(center);
      mesh.geometry?.translate(-center.x, -center.y, -center.z);

      const size = new Vector3();
      box.getSize(size);
      const maxAxis = Math.max(size.x, size.y, size.z) || 1;
      const scaleValue = 10 / maxAxis;

      setScaleArr([scaleValue, scaleValue, scaleValue]);
      setPositionArr([0, 0, 0]);
      setModel(mesh);

      const volume = mesh.geometry ? calculateVolume(mesh.geometry) : 0;
      const area = mesh.geometry ? calculateSurfaceArea(mesh.geometry) : 0;
      const weight = volume * (currentDencity || 1);
      onStatsUpdate({ volume, area, weight, dimensions: size });
    };

    if (ext === "stl") {
      const loader = new STLLoader();
      loader.load(url, (geometry) => {
        const mesh = new Mesh(geometry, createMaterial(shading, currentColor));
        processMesh(mesh);
      }, (xhr) => setLoadingProgress(xhr.total ? Math.round((xhr.loaded / xhr.total) * 100) : 0), console.error);
    } else if (ext === "obj") {
      const loader = new OBJLoader();
      loader.load(url, (obj) => {
        obj.traverse((child) => {
          if (child.isMesh) child.material = createMaterial(shading, currentColor);
        });
        processMesh(obj);
      }, (xhr) => setLoadingProgress(xhr.total ? Math.round((xhr.loaded / xhr.total) * 100) : 0), console.error);
    }
  }, [url, shading, currentColor, currentDencity]);

  // ---update material ---


  useEffect(() => {
    if (!model) return;
    model.traverse((child) => {
      if (child.isMesh && child.material) {
        child.material.dispose();
        child.material = createMaterial(shading, currentColor);
        child.material.wireframe = wireframe;
        child.material.needsUpdate = true;
      }
    });
  }, [model, shading, currentColor, wireframe]);

  // ---Canvas inner ---


  const CanvasInner = () => {
    useFrame(() => {
      if (autoRotate && primitiveRef.current) {
        primitiveRef.current.rotation.y += 0.003;
      }
    });

    return (
      <>
        <ambientLight intensity={0.4} />
        <ambientLight intensity={0.25} />
        <hemisphereLight skyColor="#e0e0e0" groundColor="#202020" intensity={0.35} />

        <Environment preset="studio" background={false} intensity={0.10} />
        {model && <primitive ref={primitiveRef} object={model} scale={scaleArr} position={positionArr} rotation={[4.7, 0, 0]} />}
        <OrbitControls enableZoom enablePan />
{/*         <Stats /> */}
        <Preload all />
      </>
    );
  };

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <button className="btn btn-sm btn-outline-secondary" onClick={() => setWireframe(!wireframe)}>Wireframe</button>
        <select className="form-select form-select-sm" value={shading} onChange={(e) => setShading(e.target.value)}>
          <option value="matte">Matte</option>
          <option value="neutral">Neutral</option>
          <option value="metal">Metallic</option>
          <option value="ceramic">Ceramic</option>
          <option value="carbon">Carbon</option>
          <option value="glass">Glass</option>
        </select>
        <button className="btn btn-sm btn-outline-secondary" onClick={() => setBg(bg === "#111827" ? "#f4f4f4" : "#111827")}>Toggle BG</button>
        <button className="btn btn-sm btn-outline-success" onClick={() => setAutoRotate(!autoRotate)}>{autoRotate ? "Stop Rotate" : "Auto Rotate"}</button>
        <button className="btn btn-sm btn-outline-secondary" onClick={() => containerRef.current?.requestFullscreen?.()}>Fullscreen</button>
      </div>

      <div style={{ width: "100%", height: "70vh", borderRadius: 8, overflow: "hidden", background: bg, position: "relative" }}>
        <Canvas shadows camera={{ position: [20, 20, 20], fov: 28 }}>
          <Suspense fallback={null}>
            <CanvasInner />
          </Suspense>
        </Canvas>

        {loadingProgress < 100 && <LoaderOverlay progress={loadingProgress} />}
      </div>
    </div>
  );
};

export default STLViewerUltraPremiumPro;
